import { FC } from 'react'
import { useToggle } from 'react-use'
import classnames from 'classnames'

import { getLogDateBlockDateString } from '../../../components/Logs/helpers'
import UserPageLink from '../../../components/UserPageLink/UserPageLink'
import { translate } from '../../../helpers/translations/translator'
import { TableCell, TableRow } from '../../components'
import {
    EventLog,
    EventLogDataItem,
    EventLogEntityTypeResource,
} from '../../entities/EventLog/EventLog'
import { formatDate } from '../../helpers/date'
import { generateUuid } from '../../helpers/string'
import trans from '../../helpers/trans'
import { IconButton, ValkTable } from '../index'
import { convertDataItemObjectToValues } from '../PacoLogsModal/helpers'
import { findShiftStartAndEndValues, isRedundantShiftPlanningLog } from './helpers'

import './PacoDetailLogs.scss'


export interface PacoDetailLogsProps {
    isFirstLogActive?: boolean;
    detailLogs: EventLog[];
    className?: string;
}

const PacoDetailLogs: FC<PacoDetailLogsProps> = ({
    isFirstLogActive = false,
    detailLogs,
    className = '',
}) => {
    const [isActive, toggleIsActive] = useToggle(isFirstLogActive)

    const lastEventLog = detailLogs[detailLogs.length - 1]

    const dateString = detailLogs?.length
        ? getLogDateBlockDateString(lastEventLog.updatedAt, new Date())
        : ''

    const textChangesCount = trans('compositions.pacoDetailLogs.textChangesCount', { count: detailLogs.length })

    const buttonText = `${dateString} - ${textChangesCount}`

    const hasOneLog = detailLogs.length === 1

    const buttonClassName = classnames('paco-detail-logs__date-button', {
        'paco-detail-logs__date-button--is-active': isActive,
    })

    const logsClassName = classnames('paco-detail-logs__logs', {
        'paco-detail-logs__logs--has-one-log': hasOneLog,
    })

    return (
        <div className={`paco-detail-logs ${className}`}>
            <IconButton
                icon="chevron-down"
                iconPos="right"
                text={buttonText}
                onClick={toggleIsActive}
                className={buttonClassName}
                iconClassName="paco-detail-logs__date-button-icon"
            />

            <div className={logsClassName}>
                {detailLogs.map(detailLog => {
                    const updatedAtDate = `${formatDate(detailLog.updatedAt, 'HH:mm')} ${translate('common.by').toLowerCase()} `

                    return (
                        <div
                            key={detailLog.id}
                            className="paco-detail-logs__log"
                        >
                            {! hasOneLog && (
                                <div className="paco-detail-logs__circle" />
                            )}

                            <div className="paco-detail-logs__mutations">
                                <div className="paco-detail-logs__title">
                                    <span className="paco-detail-logs__bold">
                                        {detailLog.message}
                                    </span>

                                    <span>
                                        {updatedAtDate}

                                        <UserPageLink
                                            name={detailLog.createdBy}
                                            id={detailLog.id}
                                            className="paco-detail-logs__actor-link"
                                        />
                                    </span>
                                </div>

                                <ValkTable<EventLogDataItem>
                                    hasRowClick
                                    rows={detailLog.items}
                                    className="paco-detail-logs__table-container"
                                    tableClassName="paco-detail-logs__table"
                                >
                                    {logs => logs.flatMap(log => {
                                        const isShiftLog = log.type === EventLogEntityTypeResource.shift
                                        const detailRows = convertDataItemObjectToValues<string>(log)

                                        const { shiftStartValue, shiftEndValue } = findShiftStartAndEndValues(detailRows)

                                        return detailRows.flatMap(({ key, field, value }) => {
                                            if (isRedundantShiftPlanningLog(isShiftLog, key, field, value, shiftStartValue, shiftEndValue)) {
                                                return []
                                            }

                                            const translatedField = trans(`entities.eventLog.categoryItem.${field}`)

                                            return (
                                                <TableRow key={generateUuid()} className="paco-detail-logs__subrow">
                                                    <TableCell
                                                        contentClassName="paco-detail-logs__content-field"
                                                        className="paco-detail-logs__value-cell"
                                                    >
                                                        {translatedField}
                                                    </TableCell>

                                                    <TableCell
                                                        contentClassName="paco-detail-logs__content-value-change"
                                                        className="paco-detail-logs__value-cell"
                                                    >
                                                        {value}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })
                                    })}
                                </ValkTable>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default PacoDetailLogs
